import {
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
  Alert,
  Divider,
  Skeleton,
  TextField,
} from "@mui/material";
import { gql } from "@apollo/client";
import { useState, useMemo } from "react";
import { isEqual, pick } from "lodash";
import { useCurrentOrg } from "~/providers/GraphqlProvider";
import { DEFAULT_SHUFFLER_LEAD_TIME_MINUTES, getOrgConfigWithDefaults, OrgConfig } from "@caredna-marlin/utils/organizationTypes";
import Help from "~/components/common/Help";
import { useUpdateOrganizationSettingsMutation } from "~/graphql/__generated__/graphql";

const FIELDS = ["enableMobileNotifications", "mobileNotificationsWindow", "enableLocationBasedStatusUpdates", "shufflerLeadTime"] as const

// Local type for UI state that allows null for empty form fields
type LocalOrgConfig = Pick<OrgConfig, typeof FIELDS[number]>;

const TabSettings = () => {
  const currentOrg = useCurrentOrg();

  const allowedRanges: Record<string, {min: number, max: number}> = {
    mobileNotificationsWindow: {min: 1, max: 72},
  };

  const formErrorDefaults = {
    mobileNotificationsWindow: false,
  };
  const [formErrors, setFormErrors] = useState<Record<string, boolean>>(formErrorDefaults);

  const originalConfig = useMemo(
    () => pick(getOrgConfigWithDefaults(currentOrg?.settings), FIELDS),
    [currentOrg?.settings]
  );

  // Convert to local config type for UI state
  const [localConfig, setLocalConfig] = useState<LocalOrgConfig>({ ...originalConfig });

  const anyErrors = Object.keys(formErrors).some((key) => formErrors[key])

  const [updateOrganizationSettings, { loading: updateLoading, error }] = useUpdateOrganizationSettingsMutation(
    { refetchQueries: ["GetAvailableOrgs"], awaitRefetchQueries: true }
  );

  // Compare with original config, treating null as the default value
  const noLocalChanges = isEqual(localConfig, originalConfig);

  const onSave = async () => {
    await updateOrganizationSettings({ variables: localConfig });
  };

  const onReset = () => {
    setLocalConfig({...originalConfig });
    setFormErrors(formErrorDefaults);
  };

  const handleToggleChange = (checked: boolean) => {
    setLocalConfig({
      ...localConfig,
      enableMobileNotifications: checked,
    });
  };

  const handleWindowChange = (field: string, input: string) => {
    const value = input == "" ? null : parseInt(input);
    
    if(value === null || !isNaN(value)) {
      setLocalConfig({
        ...localConfig,
        [field]: value,
      });
    }

    const hasError = value === null || isNaN(value) || value < allowedRanges[field].min || value > allowedRanges[field].max;
    setFormErrors({
      ...formErrors,
      [field]: hasError
    })
  };

  const handleLocationBasedStatusUpdatesChange = (checked: boolean) => {
    setLocalConfig({
      ...localConfig,
      enableLocationBasedStatusUpdates: checked,
    });
  };

  const handleShufflerLeadTimeChange = (value: number) => {
    setLocalConfig({
      ...localConfig,
      shufflerLeadTime: value,
    });
  };

  if (!currentOrg) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
        <Skeleton variant="rectangular" width="100%" height={200} />
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to update settings: {error.message}
        </Alert>
      )}

      <Card sx={{ height: "100%", pl: 1 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Mobile Settings
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={localConfig.enableMobileNotifications}
                onChange={(e) => handleToggleChange(e.target.checked)}
                disabled={updateLoading}
              />
            }
            label={<Typography>Enable Mobile Notifications
              <Help text={`Providers are notified via the mobile app when visits are scheduled or rescheduled into or out of a ${allowedRanges.mobileNotificationsWindow.min}-${allowedRanges.mobileNotificationsWindow.max} hour notification window.`} />
            </Typography>}
            sx={{ mb: 3 }}
          />

          <TextField
            label="Notification Window (hours)"
            type="number"
            value={localConfig.mobileNotificationsWindow ?? ''}
            onChange={(e) => handleWindowChange("mobileNotificationsWindow", e.target.value)}
            disabled={!localConfig.enableMobileNotifications || updateLoading}
            error={formErrors.mobileNotificationsWindow}
            size="small"
            slotProps={{
              htmlInput: {
                min: allowedRanges.mobileNotificationsWindow.min,
                max: allowedRanges.mobileNotificationsWindow.max,
              }
            }}
            sx={{ minWidth: 180, maxWidth: 300 }}
          />
          <br/>
          <FormControlLabel
            control={
              <Switch
                checked={localConfig.enableLocationBasedStatusUpdates}
                onChange={(e) => handleLocationBasedStatusUpdatesChange(e.target.checked)}
                disabled={updateLoading}
              />
            }
            label="Enable Location-Based Status Updates"
          />

          <Divider sx={{ m: 3 }} />
          <Typography variant="h6" sx={{ mb: 2 }}>
            Schedule Optimizer Settings
          </Typography>
          <FormControl size="small" sx={{ mb: 3 }}>
            <FormControlLabel
              labelPlacement="start"
              sx={{ ml: 0, mb: 3 }}
              control={
                <Select
                  value={localConfig.shufflerLeadTime ?? DEFAULT_SHUFFLER_LEAD_TIME_MINUTES}
                  onChange={(e) => handleShufflerLeadTimeChange(Number(e.target.value))}
                  disabled={updateLoading}
                  size="small"
                  sx={{ minWidth: 180, maxWidth: 300, mb: 1, ml: 3 }}
                >
                  {[DEFAULT_SHUFFLER_LEAD_TIME_MINUTES, 15, 30, 60, 90].map((min) => (
                    <MenuItem key={min} value={min}>{min}</MenuItem>
                  ))}
                </Select>
              }
              label={
                <Typography>
                  Lead Time
                  <Help text="Prevent the system from moving visits within a certain number of minutes from the current time." />
                </Typography>
              }
            />
          </FormControl>
        </CardContent>

        <CardActions>
          <Button 
            variant="contained" 
            size="small" 
            color="primary" 
            disabled={anyErrors || noLocalChanges || updateLoading} 
            onClick={onSave}
          >
            {updateLoading ? "Saving..." : "Save"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="secondary"
            disabled={noLocalChanges || updateLoading}
            onClick={onReset}
            sx={{ ml: 1 }}
          >
            Reset
          </Button>
        </CardActions>
      </Card>
    </Container>
  );
};

gql(`
  mutation UpdateOrganizationSettings(
    $enableMobileNotifications: Boolean!
    $mobileNotificationsWindow: Int!
    $enableLocationBasedStatusUpdates: Boolean!
    $shufflerLeadTime: Int!
  )
  @permission(requires: manageOrgConfig)
  {
    updateOrganizationSettings(
      enableMobileNotifications: $enableMobileNotifications
      mobileNotificationsWindow: $mobileNotificationsWindow
      enableLocationBasedStatusUpdates: $enableLocationBasedStatusUpdates
      shufflerLeadTime: $shufflerLeadTime
    ) { id }
  }
`);

export default TabSettings;
